/*
 * Copyright (c) 2025-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { parseThemeCSS, generateThemeId } from "./themeParser";
import type { Theme } from "@/config/themes";

// Import all theme CSS files from the themes directory
const freeThemes = import.meta.glob("/src/themes/*.css", {
  query: "?raw",
  import: "default",
  eager: true,
});

// Import premium theme CSS files (may not exist in development)
const premiumThemes = import.meta.glob("/src/themes/premium/*.css", {
  query: "?raw",
  import: "default",
  eager: true,
});

/**
 * Load all themes from the themes directory
 */
export function loadThemes(): Theme[] {
  const themes: Theme[] = [];

  // Process free theme files
  for (const [path, cssContent] of Object.entries(freeThemes)) {
    if (typeof cssContent !== "string") {
      console.warn(`Invalid theme file: ${path}`);
      continue;
    }

    const parsedTheme = parseThemeCSS(cssContent);
    if (!parsedTheme) {
      console.warn(`Failed to parse theme: ${path}`);
      continue;
    }

    // Extract filename without extension for fallback ID
    const filename = path.split("/").pop()?.replace(".css", "") || "unknown";

    const theme: Theme = {
      id: generateThemeId(parsedTheme.metadata.name) || filename,
      name: parsedTheme.metadata.name,
      description: parsedTheme.metadata.description,
      isPremium: parsedTheme.metadata.isPremium,
      lightOnly: parsedTheme.metadata.lightOnly,
      variations: parsedTheme.variations,
      cssVars: parsedTheme.cssVars,
    };

    themes.push(theme);
  }

  // Process premium theme files (if available)
  for (const [path, cssContent] of Object.entries(premiumThemes)) {
    if (typeof cssContent !== "string") {
      console.warn(`Invalid premium theme file: ${path}`);
      continue;
    }

    const parsedTheme = parseThemeCSS(cssContent);
    if (!parsedTheme) {
      console.warn(`Failed to parse premium theme: ${path}`);
      continue;
    }

    // Extract filename without extension for fallback ID
    const filename = path.split("/").pop()?.replace(".css", "") || "unknown";

    const theme: Theme = {
      id: generateThemeId(parsedTheme.metadata.name) || filename,
      name: parsedTheme.metadata.name,
      description: parsedTheme.metadata.description,
      isPremium: parsedTheme.metadata.isPremium ?? true, // Default to premium for premium directory
      lightOnly: parsedTheme.metadata.lightOnly,
      variations: parsedTheme.variations,
      cssVars: parsedTheme.cssVars,
    };

    themes.push(theme);
  }

  // Add default theme if no themes are loaded
  if (themes.length === 0) {
    themes.push(getDefaultTheme());
  }

  // Sort themes to ensure "minimal" is first
  themes.sort((a, b) => {
    if (a.id === "minimal") return -1;
    if (b.id === "minimal") return 1;
    return a.name.localeCompare(b.name);
  });

  // Debug log in development
  if (import.meta.env.DEV) {
    console.log("Loaded themes:", themes.map(t => ({ id: t.id, name: t.name, isPremium: t.isPremium })));
  }

  return themes;
}

/**
 * Get default theme (fallback)
 */
function getDefaultTheme(): Theme {
  return {
    id: "default",
    name: "Minimal",
    description: "Clean and minimal theme with neutral colors",
    cssVars: {
      light: {
        "--background": "oklch(1 0 0)",
        "--foreground": "oklch(0.1450 0 0)",
        "--card": "oklch(1 0 0)",
        "--card-foreground": "oklch(0.1450 0 0)",
        "--popover": "oklch(1 0 0)",
        "--popover-foreground": "oklch(0.1450 0 0)",
        "--primary": "oklch(0.2050 0 0)",
        "--primary-foreground": "oklch(0.9850 0 0)",
        "--secondary": "oklch(0.9700 0 0)",
        "--secondary-foreground": "oklch(0.2050 0 0)",
        "--muted": "oklch(0.9700 0 0)",
        "--muted-foreground": "oklch(0.5560 0 0)",
        "--accent": "oklch(0.9700 0 0)",
        "--accent-foreground": "oklch(0.2050 0 0)",
        "--destructive": "oklch(0.5770 0.2450 27.3250)",
        "--destructive-foreground": "oklch(1 0 0)",
        "--border": "oklch(0.9220 0 0)",
        "--input": "oklch(0.9220 0 0)",
        "--ring": "oklch(0.7080 0 0)",
        "--chart-1": "oklch(0.8100 0.1000 252)",
        "--chart-2": "oklch(0.6200 0.1900 260)",
        "--chart-3": "oklch(0.5500 0.2200 263)",
        "--chart-4": "oklch(0.4900 0.2200 264)",
        "--chart-5": "oklch(0.4200 0.1800 266)",
        "--sidebar": "oklch(0.9850 0 0)",
        "--sidebar-foreground": "oklch(0.1450 0 0)",
        "--sidebar-primary": "oklch(0.2050 0 0)",
        "--sidebar-primary-foreground": "oklch(0.9850 0 0)",
        "--sidebar-accent": "oklch(0.9700 0 0)",
        "--sidebar-accent-foreground": "oklch(0.2050 0 0)",
        "--sidebar-border": "oklch(0.9220 0 0)",
        "--sidebar-ring": "oklch(0.7080 0 0)",
        "--font-sans": "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
        "--font-serif": "ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
        "--font-mono": "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
        "--radius": "0.625rem",
        "--shadow-2xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
        "--shadow-xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
        "--shadow-sm": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
        "--shadow": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-md": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 2px 4px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-lg": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 4px 6px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-xl": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 8px 10px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-2xl": "0 1px 3px 0px hsl(0 0% 0% / 0.25)",
        "--tracking-normal": "0em",
        "--spacing": "0.25rem",
      },
      dark: {
        "--background": "oklch(0.1450 0 0)",
        "--foreground": "oklch(0.9850 0 0)",
        "--card": "oklch(0.2050 0 0)",
        "--card-foreground": "oklch(0.9850 0 0)",
        "--popover": "oklch(0.2690 0 0)",
        "--popover-foreground": "oklch(0.9850 0 0)",
        "--primary": "oklch(0.9220 0 0)",
        "--primary-foreground": "oklch(0.2050 0 0)",
        "--secondary": "oklch(0.2690 0 0)",
        "--secondary-foreground": "oklch(0.9850 0 0)",
        "--muted": "oklch(0.2690 0 0)",
        "--muted-foreground": "oklch(0.7080 0 0)",
        "--accent": "oklch(0.3710 0 0)",
        "--accent-foreground": "oklch(0.9850 0 0)",
        "--destructive": "oklch(0.7040 0.1910 22.2160)",
        "--destructive-foreground": "oklch(0.9850 0 0)",
        "--border": "oklch(0.2750 0 0)",
        "--input": "oklch(0.3250 0 0)",
        "--ring": "oklch(0.5560 0 0)",
        "--chart-1": "oklch(0.8100 0.1000 252)",
        "--chart-2": "oklch(0.6200 0.1900 260)",
        "--chart-3": "oklch(0.5500 0.2200 263)",
        "--chart-4": "oklch(0.4900 0.2200 264)",
        "--chart-5": "oklch(0.4200 0.1800 266)",
        "--sidebar": "oklch(0.2050 0 0)",
        "--sidebar-foreground": "oklch(0.9850 0 0)",
        "--sidebar-primary": "oklch(0.4880 0.2430 264.3760)",
        "--sidebar-primary-foreground": "oklch(0.9850 0 0)",
        "--sidebar-accent": "oklch(0.2690 0 0)",
        "--sidebar-accent-foreground": "oklch(0.9850 0 0)",
        "--sidebar-border": "oklch(0.2750 0 0)",
        "--sidebar-ring": "oklch(0.4390 0 0)",
        "--font-sans": "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
        "--font-serif": "ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
        "--font-mono": "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
        "--radius": "0.625rem",
        "--shadow-2xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
        "--shadow-xs": "0 1px 3px 0px hsl(0 0% 0% / 0.05)",
        "--shadow-sm": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
        "--shadow": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-md": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 2px 4px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-lg": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 4px 6px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-xl": "0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 8px 10px -1px hsl(0 0% 0% / 0.10)",
        "--shadow-2xl": "0 1px 3px 0px hsl(0 0% 0% / 0.25)",
        "--tracking-normal": "0em",
        "--spacing": "0.25rem",
      },
    },
  };
}
